import { useEffect, useMemo } from 'react'
import annotationPlugin from 'chartjs-plugin-annotation'

export const CHART_SWEEP_DURATION = 2000

export const CHART_ANNOTATION_DELAY = 150
export const CHART_ANNOTATION_DURATION = 700

export const CHART_ANNOTATION_STAGGER = 140

export const sweepClipPlugin = {
	id: 'postgameSweep',

	beforeDatasetsDraw(chart) {
		const progress = chart.$sweepProgress
		if (progress === undefined || progress >= 1) return

		const { left, top, right, bottom } = chart.chartArea
		chart.ctx.save()
		chart.ctx.beginPath()
		chart.ctx.rect(left, top, (right - left) * progress, bottom - top)
		chart.ctx.clip()
	},

	afterDatasetsDraw(chart) {
		const progress = chart.$sweepProgress
		if (progress === undefined || progress >= 1) return
		chart.ctx.restore()
	},
}

const clamp01 = (value) => Math.min(Math.max(value, 0), 1)
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)

const annotationElements = (chart) => annotationPlugin._getState?.(chart)?.elements ?? []

function readMarkerTargets(chart) {
	return annotationElements(chart).map((element) => {
		const label = element.elements?.[0]
		return {
			y2: element.y2,
			labelY: label?.y,
			labelCenterY: label?.centerY,
		}
	})
}

function applyMarkers(chart, targets, elapsed) {
	const top = chart.chartArea.top
	const elements = annotationElements(chart)

	for (let index = 0; index < elements.length; index++) {
		const element = elements[index]
		const target = targets[index]
		if (!target) continue

		const start = CHART_ANNOTATION_DELAY + index * CHART_ANNOTATION_STAGGER
		const progress = clamp01((elapsed - start) / CHART_ANNOTATION_DURATION)
		const eased = easeOutCubic(progress)
		const drop = (to) => top + (to - top) * eased

		element.y2 = drop(target.y2)

		const label = element.elements?.[0]
		if (!label) continue
		if (target.labelY !== undefined) label.y = drop(target.labelY)
		if (target.labelCenterY !== undefined) label.centerY = drop(target.labelCenterY)
		// The icon itself only fades, it is carried down by the line it belongs to
		if (label.options) label.options.opacity = progress
	}
}

export function useChartEntrance(chartRef, animate) {
	useEffect(() => {
		const chart = chartRef.current
		if (!chart) return undefined

		if (!animate) {
			chart.$sweepProgress = undefined
			return undefined
		}

		const targets = readMarkerTargets(chart)
		const total = Math.max(CHART_SWEEP_DURATION, CHART_ANNOTATION_DELAY + Math.max(targets.length - 1, 0) * CHART_ANNOTATION_STAGGER + CHART_ANNOTATION_DURATION)

		let frame
		let start

		const paint = (elapsed) => {
			const current = chartRef.current
			if (!current) return false

			current.$sweepProgress = clamp01(elapsed / CHART_SWEEP_DURATION)
			applyMarkers(current, targets, elapsed)
			current.draw()

			return elapsed < total
		}

		const step = (now) => {
			if (start === undefined) start = now
			if (paint(now - start)) frame = requestAnimationFrame(step)
		}

		paint(0)
		frame = requestAnimationFrame(step)

		return () => {
			cancelAnimationFrame(frame)
			chart.$sweepProgress = undefined
		}
	}, [chartRef, animate])
}

export function useAnnotationEntrance(annotations, animate) {
	return useMemo(() => {
		const list = annotations ?? []
		if (!animate) return list

		return [...list]
			.sort((a, b) => (a.value ?? 0) - (b.value ?? 0))
			.map((annotation) => ({
				...annotation,
				adjustScaleRange: false,
				drawTime: 'afterDraw',
			}))
	}, [annotations, animate])
}
